import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
// import { updatePositV4Orchestrator } from '@/utils/secureApiCalls'; // Now imported dynamically

// Extend window interface for audio context
declare global {
  interface Window {
    audioContext?: AudioContext;
  }
}

interface UseVoiceUpdateReturn {
  startVoiceUpdate: () => Promise<void>;
  endVoiceUpdate: () => void;
  isConnected: boolean;
  isConnecting: boolean;
  isAgentSpeaking: boolean;
  conversationId?: string;
  canRetry: boolean;
  isProcessing: boolean;
  knowledgeUpdates: string[];
}

export const useVoiceUpdate = (positId: string): UseVoiceUpdateReturn => {
  const { user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  const [knowledgeUpdates, setKnowledgeUpdates] = useState<string[]>([]);
  
  // Smart buffering state for transcript accumulation
  const [transcriptBuffer, setTranscriptBuffer] = useState<string[]>([]);
  const [lastTranscriptTime, setLastTranscriptTime] = useState<number>(0);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const isStartingRef = useRef(false);
  
  // TTS function using ElevenLabs HTTP API (same as voice conversation)
  const playTextToSpeech = useCallback(async (text: string) => {
    try {
      setIsAgentSpeaking(true);
      
      // Get API key from our edge function
      const { data: apiData, error: apiError } = await supabase.functions.invoke('elevenlabs-voice-webhook', {
        body: {
          action: 'get_api_key'
        }
      });
      
      if (apiError || !apiData?.api_key) {
        setIsAgentSpeaking(false);
        return;
      }
      
      // Use ElevenLabs HTTP API for TTS
      const voiceId = 'UgBBYS2sOqTuMpoF3BR0'; // User's preferred voice
      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiData.api_key
        },
        body: JSON.stringify({
          text: text,
          model_id: 'eleven_monolingual_v1',
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.8
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`TTS API error: ${response.status}`);
      }
      
      // Convert response to audio blob and play
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      // Store reference for interruption capability
      currentAudioRef.current = audio;
      
      audio.play()
        .then(() => {
          audio.onended = () => {
            URL.revokeObjectURL(audioUrl);
            setIsAgentSpeaking(false);
            currentAudioRef.current = null; // Clear reference when done
          };
        })
        .catch(error => {
          URL.revokeObjectURL(audioUrl);
          setIsAgentSpeaking(false);
          currentAudioRef.current = null; // Clear reference on error
        });
        
    } catch (error) {
      setIsAgentSpeaking(false);
    }
  }, []);

  // Process accumulated transcripts through UPDATE system
  const processAccumulatedTranscripts = useCallback(async (combinedMessage: string) => {
    
    // Stop any currently playing TTS audio (interruption)
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsAgentSpeaking(false);
    }
    
    // Clear transcript buffer since we're processing
    setTranscriptBuffer([]);
    
    setIsProcessing(true);
    
    try {
      // USE NEW V4 ORCHESTRATOR - immediate response + background processing
      const { updatePositV4Orchestrator } = await import('@/utils/secureApiCalls');
      const data = await updatePositV4Orchestrator(
        combinedMessage,
        positId,
        user?.id || '',
        null, // sessionId - use null to avoid FK constraint issues
        undefined // positTitle - will be fetched by function
      );

      // Track knowledge updates - for V4 orchestrator, we know processing is happening in background
      const updates: string[] = [];
      
      if (data.metadata.background_processing) {
        updates.push('Knowledge extraction in progress');
        updates.push('Vector updates queued');
        updates.push('Profile being enhanced');
      }
      
      if (updates.length > 0) {
        setKnowledgeUpdates(prev => [...prev, ...updates]);
      }

      // Send response to TTS function for audio generation
      if (data.content) {
        await playTextToSpeech(data.content);
      }

    } catch (error) {
    } finally {
      setIsProcessing(false);
    }
  }, [user?.id, positId, conversationId, playTextToSpeech]);

  // Smart transcript buffering with silence detection
  const handleTranscriptReceived = useCallback((transcript: string) => {
    
    // Don't buffer if already processing
    if (isProcessing) {
      return;
    }
    
    // Add to buffer and update timestamp
    setTranscriptBuffer(prev => [...prev, transcript]);
    setLastTranscriptTime(Date.now());
    
    // Clear existing timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }
    
    // Set new timeout for silence detection (2 seconds)
    silenceTimeoutRef.current = setTimeout(() => {
      setTranscriptBuffer(currentBuffer => {
        if (currentBuffer.length > 0) {
          const combinedTranscript = currentBuffer.join(' ').trim();
          processAccumulatedTranscripts(combinedTranscript);
          return []; // Clear buffer
        }
        return currentBuffer;
      });
    }, 2000); // 2 second silence threshold
  }, [isProcessing, processAccumulatedTranscripts]);

  // Cleanup function for silence timeout
  useEffect(() => {
    return () => {
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
      }
    };
  }, []);

  // Audio capture functions (same as voice conversation)
  const startAudioCapture = useCallback(async () => {
    try {
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      // Create audio context for PCM conversion
      if (!window.audioContext) {
        window.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      
      const audioContext = window.audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (event) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const inputBuffer = event.inputBuffer.getChannelData(0);
          
          // Convert Float32 to Int16 PCM
          const pcmData = new Int16Array(inputBuffer.length);
          for (let i = 0; i < inputBuffer.length; i++) {
            pcmData[i] = Math.max(-32768, Math.min(32767, inputBuffer[i] * 32768));
          }
          
          // Convert to base64 and send
          const base64Data = btoa(String.fromCharCode(...new Uint8Array(pcmData.buffer)));
          
          wsRef.current.send(JSON.stringify({
            type: 'user_audio_chunk',
            chunk: base64Data
          }));
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      // Store processor reference for cleanup
      (stream as any).processor = processor;
      (stream as any).source = source;

      mediaRecorderRef.current = stream as any;
      
    } catch (error) {
      throw error;
    }
  }, []);

  const stopAudioCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      
      const stream = mediaRecorderRef.current as any;
      
      // Clean up audio processing
      if (stream.processor) {
        stream.processor.disconnect();
      }
      if (stream.source) {
        stream.source.disconnect();
      }
      
      // Stop all tracks
      if (stream.getTracks) {
        stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
      }
      
      mediaRecorderRef.current = null;
    }
  }, []);

  const startVoiceUpdate = useCallback(async () => {
    if (isStartingRef.current || isConnected) {
      return;
    }

    // Wait for user to be available before starting
    if (!user?.id) {
      setTimeout(() => startVoiceUpdate(), 1000);
      return;
    }

    isStartingRef.current = true;
    setCanRetry(false);
    
    try {
      
      // Get signed URL from our edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke('elevenlabs-voice-webhook', {
        body: {
          action: 'get_signed_url',
          posit_id: positId,
          user_id: user?.id
        }
      });

      if (urlError || !urlData?.signed_url) {
        throw new Error('Failed to get signed URL');
      }


      // Create WebSocket connection
      const ws = new WebSocket(urlData.signed_url);
      wsRef.current = ws;

      ws.onopen = async () => {
        setIsConnected(true);
        setCanRetry(true);
        retryCountRef.current = 0;
        
        // Set conversation ID for voice update
        const sessionId = `voice-update-${Date.now()}`;
        setConversationId(sessionId);
        
        // Send initial configuration
        ws.send(JSON.stringify({
          type: 'conversation_initiation_client_data',
          conversation_config_override: {
            agent: {
              language: "en"
            }
          }
        }));
        
        
        // Start capturing and sending audio
        await startAudioCapture();
      };

      ws.onmessage = (event) => {
        
        try {
          const data = JSON.parse(event.data);
        
          // Handle ping events
          if (data.type === 'ping') {
            ws.send(JSON.stringify({
              type: 'pong',
              event_id: data.ping_event.event_id
            }));
            return;
          }
          
          // Handle user transcript
          if (data.type === 'user_transcript') {
            const transcript = data.user_transcription_event?.user_transcript;
            if (transcript) {
              handleTranscriptReceived(transcript);
            }
          }
          
        } catch (parseError) {
        }
      };

      ws.onclose = (event) => {
        
        // Stop any currently playing TTS audio
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }
        
        setIsConnected(false);
        setIsAgentSpeaking(false);
        setIsProcessing(false);
        isStartingRef.current = false;
        stopAudioCapture();
        
      };

      ws.onerror = (error) => {
        setIsConnected(false);
        setCanRetry(true);
        isStartingRef.current = false;
        stopAudioCapture();
      };

    } catch (error) {
      setIsConnected(false);
      setCanRetry(true);
      isStartingRef.current = false;
      stopAudioCapture();
      
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        setTimeout(() => startVoiceUpdate(), 2000);
      }
    }
  }, [user?.id, positId, isConnected, handleTranscriptReceived, startAudioCapture]);

  const endVoiceUpdate = useCallback(() => {
    
    // Stop any currently playing TTS audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    // Clear transcript buffer and timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    setTranscriptBuffer([]);
    
    stopAudioCapture();
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setIsAgentSpeaking(false);
    setIsProcessing(false);
    setConversationId(null);
    setKnowledgeUpdates([]);
    isStartingRef.current = false;
  }, [stopAudioCapture]);

  // Cleanup on unmount and browser navigation
  useEffect(() => {
    // Handle browser back button and page unload
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      endVoiceUpdate();
      // Don't show confirmation dialog for voice sessions
      event.preventDefault();
    };

    const handleUnload = () => {
      endVoiceUpdate();
    };

    // Add event listeners for browser navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('unload', handleUnload);

    return () => {
      
      // Remove event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
      
      // Always end update session on unmount
      endVoiceUpdate();
    };
  }, [endVoiceUpdate, isConnected, user]);

  return {
    isConnected,
    isConnecting,
    isProcessing,
    isAgentSpeaking,
    conversationId,
    canRetry,
    knowledgeUpdates,
    startVoiceUpdate,
    endVoiceUpdate
  };
}; 